import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import style from './FeaturedStories.module.scss';
import indigenousPoster from '../imgs/themePosters/indigenous.jpg';
import colonialPoster from '../imgs/themePosters/colonial.jpg';
import communitiesPoster from '../imgs/themePosters/our-communities.jpg';
import industrialPoster from '../imgs/themePosters/industrial.jpg';
import sitesPoster from '../imgs/themePosters/sites-landmarks.jpg';
import environmentPoster from '../imgs/themePosters/environment.jpg';
import artsPoster from '../imgs/themePosters/arts-monuments.jpg';

// Theme label (`loc`) matched strictly by video UUID against the pre-removal
// Themes wiring (git HEAD:src/components/Themes.js).
const STORIES = [
    {
        loc: 'Colonial',
        title: 'Female Factory Parramatta',
        category: 'Some estimates report 1 in 5 to 1 in 7 people in Australia are related to the women who went through the female factory in Parramatta.',
        thumbnail: colonialPoster,
        videoUrl: 'https://media.yesterdaystories.net.au/prod/dfdde806-76b1-41e0-8bd8-802c34a9d95f.mp4',
    },
    {
        loc: 'Industrial',
        title: 'Mt Kembla disaster',
        category: "Mt Kembla - Australia's Largest industrial disaster.",
        thumbnail: industrialPoster,
        videoUrl: 'https://media.yesterdaystories.net.au/prod/b823bff7-ab46-4f5d-880d-5bb44ad002d6.mp4',
    },
    {
        loc: 'Indigenous',
        title: 'Mungo National Park',
        category: 'Mungo Man and Mungo Woman were buried with special ceremony over 50,000 years ago. Their discovery provides evidence of continued occupation of the land by 17,000 generations of Aboriginal people.',
        thumbnail: indigenousPoster,
        videoUrl: 'https://media.yesterdaystories.net.au/prod/bc1b686e-6a53-42a8-8c85-6dbbf6bf8790.mp4',
    },
    {
        loc: 'our communities',
        title: 'Fishing across Lands',
        category: 'No one loves their job more than Rocco! Fishing story set on Wollongong Harbour',
        thumbnail: communitiesPoster,
        videoUrl: 'https://media.yesterdaystories.net.au/prod/68839edf-f027-4487-bbc9-fdb7df82e520.mp4',
    },
    {
        loc: 'Sites & Landmarks',
        title: 'Old Goulburn Brewery',
        category: 'The women were the maltsters as they could sense the change in weather, humidity and were more delicate with the barley.',
        thumbnail: sitesPoster,
        videoUrl: 'https://media.yesterdaystories.net.au/prod/a2b439df-e129-4cde-9889-f2c328922cf8.mp4',
    },
    {
        loc: 'Environment',
        title: 'Aboriginal Fishtraps Brewarrina',
        category: 'The Brewarrina Fishtraps are possibly the oldest man-made structures on earth. Built by Aboriginal people thousands of years ago, they have been continuously used as an aquacultural resource and a trading and gathering place up until the present day.',
        thumbnail: environmentPoster,
        videoUrl: 'https://media.yesterdaystories.net.au/prod/f9f50373-1a23-4fb7-a701-39b3437f84a2.mp4',
    },
    {
        loc: 'Arts & Monuments',
        title: 'Haymarket-WW2 Monument',
        category: "The Chinese Ex-Servicemen's Memorial in Haymarket commemorates the proud history of Chinese-Australian military service going back as far as WWI.",
        thumbnail: artsPoster,
        videoUrl: 'https://media.yesterdaystories.net.au/prod/4da5a1f0-d343-4d8f-b97b-6f98a258bb2c.mp4',
    },
];

const StoryCard = ({ story }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [expanded, setExpanded] = useState(false);
    const [isTruncated, setIsTruncated] = useState(false);
    const descRef = useRef(null);
    const canPlay = Boolean(story.videoUrl);

    // Does the description overflow the 3-line clamp? (only meaningful while collapsed)
    useEffect(() => {
        const el = descRef.current;
        if (!el || expanded) return undefined;
        const measure = () => setIsTruncated(el.scrollHeight - el.clientHeight > 1);
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [expanded, story.category]);

    // Animate to the exact content height so expand/collapse is smooth (no overshoot).
    useLayoutEffect(() => {
        const el = descRef.current;
        if (!el) return;
        el.style.maxHeight = expanded ? `${el.scrollHeight}px` : '';
    }, [expanded]);

    return (
        <div className={style.storyCard}>
            <div
                className={style.frame}
                onClick={canPlay ? () => setIsPlaying(true) : undefined}
                style={canPlay ? { cursor: 'pointer' } : undefined}
            >
                {isPlaying ? (
                    <video className={style.video} controls autoPlay>
                        <source src={story.videoUrl} type="video/mp4" />
                    </video>
                ) : (
                    <>
                        {story.thumbnail ? (
                            <img className={style.thumbImg} src={story.thumbnail} alt={story.title} />
                        ) : (
                            canPlay && (
                                // No poster image for these — show the video's first frame as the thumbnail.
                                <video className={style.thumbImg} src={`${story.videoUrl}#t=0.1`} preload="metadata" muted playsInline tabIndex={-1} />
                            )
                        )}
                        {story.duration && <div className={style.duration}>{story.duration}</div>}
                        <div className={style.playSm}>
                            <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M3 1.5 10 6 3 10.5V1.5Z" fill="#1B2A3D" /></svg>
                        </div>
                    </>
                )}
            </div>
            <div className={style.meta}>
                {story.loc && <div className={style.loc}>{story.loc}</div>}
                <h4>{story.title}</h4>
                {story.category && (
                    <>
                        <p
                            ref={descRef}
                            className={`${style.desc} ${expanded ? style.descExpanded : ''}`}
                        >
                            {story.category}
                        </p>
                        <button
                            type="button"
                            className={style.readMore}
                            onClick={() => setExpanded(v => !v)}
                            aria-expanded={expanded}
                            // reserve the line for uniform card heights; hide (and drop from tab order) when nothing to expand
                            style={!isTruncated && !expanded ? { visibility: 'hidden' } : undefined}
                        >
                            {expanded ? 'Show less' : 'Read more'}
                        </button>
                    </>
                )}
            </div>
        </div>
    );
};

const FeaturedStories = () => (
    <section className={style.storiesSection} id="stories">
        <div className="wrap">
            <div className={style.storiesHead}>
                <div className="section-head" style={{ margin: 0 }}>
                    <div className="eyebrow">Featured on the map</div>
                    <h2>View some of our most popular stories</h2>
                </div>
                <a href="#about" className={style.viewAll}>View all stories</a>
            </div>
            <div className={style.storiesGrid}>
                {STORIES.map(story => <StoryCard story={story} key={story.title} />)}
            </div>
        </div>
    </section>
);

export default FeaturedStories;
